import {
  type Checkpoint,
  checkpointOfLabels,
  REQUIREMENT_AGENT_LABEL,
  REQUIREMENT_DOCUMENTS_BRANCH_PREFIX,
} from '@vivicta/asdp-contract';

// File names, paths and branch prefixes the portal relies on in application
// repositories. They must match the workflows and files shipped by the
// Vivicta application templates.

export const REQUEST_IDENTITY_WORKFLOW = 'request-identity.yml';
export const IDENTITY_READY_WORKFLOW = 'identity-ready.yml';
export const APPLY_SETUP_CONFIG_WORKFLOW = 'apply-setup-config.yml';
export const SETUP_CONFIG_MERGED_WORKFLOW = 'setup-config-merged.yml';
export const CI_WORKFLOW = 'ci.yml';
export const DEPLOY_WORKFLOW = 'deploy.yml';
export const DEPLOY_PLATFORM_WORKFLOW = 'deploy-platform.yml';
export const DEPLOY_SANDBOX_WORKFLOW = 'deploy-sandbox.yml';
/** Template 1's sandbox teardown; template 2 tears down through deploy-platform.yml. */
export const TEARDOWN_SANDBOX_WORKFLOW = 'teardown-sandbox.yml';

// Build once, promote: what deploy.yml and its siblings record and read.
/** The deployment task deploy.yml, deploy-environment.yml and deploy-sandbox.yml record. */
export const APP_DEPLOYMENT_TASK = 'deploy:app';
export const PLATFORM_DEPLOYMENT_TASK = 'platform';
export const TEARDOWN_DEPLOYMENT_TASK = 'platform:teardown';
/** The status description deploy.yml adds when an environment's integration tests passed. */
export const TESTS_PASSED_STATUS = 'Integration tests passed';
/** Repository variables the deploy workflows read. */
export const DEPLOY_ENVIRONMENTS_VARIABLE = 'DEPLOY_ENVIRONMENTS';
export const TEST_PROMOTION_VARIABLE = 'TEST_PROMOTION';
export const SANDBOX_IDLE_DAYS_VARIABLE = 'SANDBOX_IDLE_DAYS';
/** sandbox-idle.yml's default when SANDBOX_IDLE_DAYS is unset. */
export const DEFAULT_SANDBOX_IDLE_DAYS = 14;
/** Organization variable: where deploy-platform.yml may place PostgreSQL and AI Search, first one first. */
export const ALLOWED_DATA_REGIONS_VARIABLE = 'ALLOWED_DATA_REGIONS';
/** deploy-platform.sh's default when the variable is unset (EU only, Sweden first). */
export const DEFAULT_DATA_REGIONS = [
  'swedencentral',
  'northeurope',
  'westeurope',
];
/** The label the project agent puts on its delivery-plan epics. */
export const DELIVERY_PLAN_LABEL = 'epic';
/** Runs the requirement agent on merged requirements, once setup is applied. */
export const REQUIREMENT_AGENT_DISPATCHER_WORKFLOW =
  'requirement-agent-dispatcher.yml';
/** The label and branch prefix of the requirement agent's documents PR. */
export { REQUIREMENT_AGENT_LABEL, REQUIREMENT_DOCUMENTS_BRANCH_PREFIX };
/** Runs the requirement agent again on a `/revise` pull request comment. */
export const REQUIREMENT_REVISION_WORKFLOW =
  'requirement-revision-dispatcher.yml';
export const PROJECT_AGENT_DISPATCHER_WORKFLOW = 'project-agent-dispatcher.yml';
/** invoke-agent.yml's check run with the agent's steps, its external_id the run's ID. */
export const AGENT_ACTIVITY_CHECK = 'Agent activity';

// Applications carry APPLICATION_TOPIC; ONBOARDING_COMPLETE_TOPIC marks one
// whose owner finished onboarding before (or without) the setup PR.
export const APPLICATION_TOPIC = 'vivicta-application';
export const ONBOARDING_COMPLETE_TOPIC = 'onboarding-complete';

export const SETUP_CONFIG_PATH = 'setup-config.yaml';
export const REQUIREMENTS_JSON_PATH = 'docs/requirements.json';
export const REQUIREMENTS_TEMPLATE_PATH = 'docs/requirements-template.md';
export const REQUIREMENTS_MARKDOWN_PATH = 'docs/requirements.md';

export const REQUIREMENTS_BRANCH_PREFIX = 'portal/requirements-';
export const SETUP_SETTINGS_BRANCH_PREFIX = 'portal/setup-settings-';

// Live in the Identity Vending Machine repository, not the application's.
export const IDENTITY_VENDING_WORKFLOW = 'handle-identity-request.yml';
export const DECOMMISSION_WORKFLOW = 'decommission-app.yml';
/** Assigns the roles a platform set-up's first pass asked for. */
export const PLATFORM_RBAC_WORKFLOW = 'handle-platform-rbac-request.yml';
export const ENVIRONMENT_RULES_WORKFLOW =
  'handle-environment-rules-request.yml';

export const APPLY_CONFIG_BRANCH_PREFIX = 'setup/apply-config-';

// ASDP checkpoint PRs carry `asdp-checkpoint:<id>`; the label wins over branch names.
export { CHECKPOINT_LABEL_PREFIX } from '@vivicta/asdp-contract';

type CheckpointCandidate = {
  title: string;
  head: { ref: string };
  labels: ReadonlyArray<{ name?: string }>;
};

/** Before checkpoint labels: the setup PRs as the templates name and label them. */
const HEURISTICS: ReadonlyArray<
  [Checkpoint, (pr: CheckpointCandidate) => boolean]
> = [
  ['apply-config', pr => pr.head.ref.startsWith(APPLY_CONFIG_BRANCH_PREFIX)],
  ['requirements', pr => pr.head.ref.startsWith(REQUIREMENTS_BRANCH_PREFIX)],
  [
    'setup-output',
    pr =>
      // Portal-created requirements PRs are user submissions, not agent output.
      !pr.head.ref.startsWith(REQUIREMENTS_BRANCH_PREFIX) &&
      (pr.head.ref.startsWith('agent/') ||
        pr.head.ref.startsWith(REQUIREMENT_DOCUMENTS_BRANCH_PREFIX) ||
        pr.title.toLowerCase().includes('requirement') ||
        pr.title.toLowerCase().includes('setup-config') ||
        pr.labels.some(label => label.name === 'ai-generated')),
  ],
];

const looksLike = (pr: CheckpointCandidate, checkpoint: Checkpoint) =>
  HEURISTICS.some(([id, test]) => id === checkpoint && test(pr));

/** Which checkpoint a pull request is: its label first, the old heuristic for older applications. */
export const checkpointOfPull = (
  pr: CheckpointCandidate,
): Checkpoint | undefined =>
  checkpointOfLabels(pr.labels) ?? HEURISTICS.find(([, test]) => test(pr))?.[0];

/**
 * The open setup PRs of an application. A PR with a checkpoint label is only
 * ever that checkpoint; unlabelled PRs are matched as before labels existed.
 */
export function findSetupPulls<T extends CheckpointCandidate>(
  prs: readonly T[],
) {
  const of = (checkpoint: Checkpoint) =>
    prs.find(pr => checkpointOfLabels(pr.labels) === checkpoint) ??
    prs.find(
      pr =>
        checkpointOfLabels(pr.labels) === undefined &&
        looksLike(pr, checkpoint),
    );
  const requirementAgentPr = prs.find(pr =>
    pr.labels.some(label => label.name === REQUIREMENT_AGENT_LABEL),
  );
  return {
    agentPr: requirementAgentPr ?? of('setup-output'),
    applyPr: of('apply-config'),
    requirementsPr: of('requirements'),
  };
}
